import { useEffect, useState } from 'react';
import { Box, FormControl, FormLabel, VStack, Flex, Heading, Text, Button, IconButton, Grid, GridItem, Input } from '@chakra-ui/react';
import { AddIcon, DeleteIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import StrongModal from '../common/StrongModal';
import StrongInput from '../common/StrongInput';
import StrongTextarea from '../common/StrongTextarea';
import StrongSingleSelect from '../common/StrongSingleSelect';
import StrongCheckBox from '../common/StrongCheckBox';
import useApi from '../../lib/hooks/useApi';
import urlConstants from '../../lib/constants/url.constants';
import { ApiCommand } from '../../lib/Api';
import { DayExercise, WarmupTemplate, Circuit } from '../../lib/types/programEdit';

interface ExerciseTitle {
  title: string;
  id: string;
  _id?: string;
}

interface EditWarmupModalProps {
  selectedWarmup?: WarmupTemplate;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    title: string,
    instructions: string,
    circuits: Circuit[],
    exercises: DayExercise[],
  ) => void;
}

const normalizeExerciseId = (value: any): string => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    return value._id || value.id || '';
  }
  return '';
};

// ─── WarmupCircuitEditor ──────────────────────────────────────────────────────
// Mirrors CircuitPlan: local rounds state, collapsible body, same layout.

type WarmupCircuitEditorProps = {
  circuit: Circuit;
  circuitIndex: number;
  exerciseOptions: { label: string; value: string }[];
  onUpdate: (key: keyof Circuit, value: any) => void;
  onAddExercise: () => void;
  onRemoveExercise: (exerciseIndex: number) => void;
  onUpdateExercise: (exerciseIndex: number, key: keyof DayExercise, value: any) => void;
  onRemoveCircuit: () => void;
};

const WarmupCircuitEditor = ({
  circuit,
  circuitIndex,
  exerciseOptions,
  onUpdate,
  onAddExercise,
  onRemoveExercise,
  onUpdateExercise,
  onRemoveCircuit,
}: WarmupCircuitEditorProps) => {
  const [roundsInput, setRoundsInput] = useState(String(circuit.rounds ?? 1));
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    setRoundsInput(String(circuit.rounds ?? 1));
  }, [circuit.rounds]);

  return (
    <Box p={4} bg="primary.50" borderRadius="md" boxShadow="sm" my={4}>
      <Flex justify="space-between" align="center" mb={4}>
        <Flex align="center" gap={3} flex="1">
          <Text fontWeight="bold" color="gray.600" whiteSpace="nowrap">Circuit {circuitIndex + 1}</Text>
          <Input
            value={circuit.title || ''}
            onChange={(e) => onUpdate('title', e.target.value)}
            fontWeight="bold"
            fontSize="md"
            maxW="220px"
            bg="white"
            size="sm"
          />
        </Flex>
        <Flex gap={2}>
          <Button
            backgroundColor="primary.300"
            leftIcon={<DeleteIcon mr="-2" />}
            _hover={{ bg: 'primary.500' }}
            size="sm"
            onClick={onRemoveCircuit}
          />
          <Button
            backgroundColor="primary.300"
            leftIcon={isCollapsed ? <ChevronDownIcon mr="-2" /> : <ChevronUpIcon mr="-2" />}
            _hover={{ bg: 'primary.500' }}
            size="sm"
            onClick={() => setIsCollapsed((prev) => !prev)}
          />
        </Flex>
      </Flex>

      {!isCollapsed && (
        <>
          <FormControl maxW="220px" mt={2} mb={4}>
            <FormLabel>Rounds</FormLabel>
            <StrongInput
              type="number"
              min={1}
              value={roundsInput}
              onChange={(e) => {
                const { value } = e.target;
                setRoundsInput(value);
                if (value === '') return;
                const next = Number(value);
                if (!Number.isNaN(next) && next >= 1) onUpdate('rounds', next);
              }}
              onBlur={() => {
                const next = Math.max(1, Number(roundsInput) || 1);
                setRoundsInput(String(next));
                onUpdate('rounds', next);
              }}
            />
          </FormControl>

          <Box pl={4} borderLeft="3px solid" borderColor="primary.200">
            {circuit.circuitExercises.length === 0 ? (
              <Box py={4} textAlign="center" bg="gray.50" borderRadius="md" mb={4}>
                <Text fontSize="sm" color="gray.500">No exercises in this circuit yet.</Text>
              </Box>
            ) : (
              circuit.circuitExercises.map((exercise, exerciseIndex) => {
                const selectedId = normalizeExerciseId(exercise.exerciseId);
                const selectedOption = exerciseOptions.find((opt) => opt.value === selectedId);

                return (
                  <Box key={exerciseIndex} p={4} borderWidth="1px" borderRadius="md" bg="white" shadow="xs" mb={4}>
                    <Flex justifyContent="space-between" alignItems="center" mb={3}>
                      <Text fontWeight="bold" color="primary.500" fontSize="sm">Exercise #{exerciseIndex + 1}</Text>
                      <IconButton
                        aria-label="Remove Exercise"
                        icon={<DeleteIcon />}
                        colorScheme="red"
                        variant="outline"
                        size="xs"
                        onClick={() => onRemoveExercise(exerciseIndex)}
                      />
                    </Flex>
                    <Grid templateColumns="repeat(6, 1fr)" gap={3}>
                      <GridItem colSpan={6}>
                        <FormControl isRequired>
                          <FormLabel fontSize="xs" color="gray.600" mb={1}>Select Exercise</FormLabel>
                          <StrongSingleSelect
                            options={exerciseOptions}
                            value={selectedOption}
                            onSelectChange={(val) => onUpdateExercise(exerciseIndex, 'exerciseId', val)}
                          />
                        </FormControl>
                      </GridItem>
                      <GridItem colSpan={6}>
                        <FormControl>
                          <FormLabel fontSize="xs" color="gray.600" mb={1}>Guideline / Instructions</FormLabel>
                          <StrongTextarea
                            value={exercise.guide || ''}
                            onChange={(e) => onUpdateExercise(exerciseIndex, 'guide', e.target.value)}
                            placeholder="Instructions..."
                            rows={2}
                            height="50px"
                          />
                        </FormControl>
                      </GridItem>
                      <GridItem colSpan={2}>
                        <FormControl>
                          <FormLabel fontSize="xs" color="gray.600" mb={1}>Sets</FormLabel>
                          <StrongInput type="number" min={1} value={exercise.sets}
                            onChange={(e) => onUpdateExercise(exerciseIndex, 'sets', Number(e.target.value) || 1)} />
                        </FormControl>
                      </GridItem>
                      <GridItem colSpan={2}>
                        <FormControl>
                          <FormLabel fontSize="xs" color="gray.600" mb={1}>Reps</FormLabel>
                          <StrongInput type="number" value={exercise.reps}
                            onChange={(e) => onUpdateExercise(exerciseIndex, 'reps', Number(e.target.value) || 0)} />
                        </FormControl>
                      </GridItem>
                      <GridItem colSpan={2}>
                        <FormControl>
                          <FormLabel fontSize="xs" color="gray.600" mb={1}>Rest (sec)</FormLabel>
                          <StrongInput type="number" value={exercise.rest}
                            onChange={(e) => onUpdateExercise(exerciseIndex, 'rest', Number(e.target.value) || 0)} />
                        </FormControl>
                      </GridItem>
                      <GridItem colSpan={4} display="flex" alignItems="center">
                        <StrongCheckBox
                          isChecked={exercise.per_side || false}
                          onCheckChange={(checked) => onUpdateExercise(exerciseIndex, 'per_side', checked)}
                        >
                          Per Side
                        </StrongCheckBox>
                      </GridItem>
                      <GridItem colSpan={6}>
                        <FormControl>
                          <FormLabel fontSize="xs" color="gray.600" mb={1}>Custom Text</FormLabel>
                          <StrongInput value={exercise.custom_text || ''}
                            onChange={(e) => onUpdateExercise(exerciseIndex, 'custom_text', e.target.value)}
                            placeholder="Optional custom text..." />
                        </FormControl>
                      </GridItem>
                    </Grid>
                  </Box>
                );
              })
            )}
            <Button
              colorScheme="primary"
              variant="outline"
              leftIcon={<AddIcon />}
              mt={2}
              size="sm"
              onClick={onAddExercise}
            >
              Add Exercise To Circuit
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

// ─── EditWarmupModal ──────────────────────────────────────────────────────────

const EditWarmupModal = ({
  selectedWarmup,
  isOpen,
  onClose,
  onSubmit,
}: EditWarmupModalProps) => {
  const [title, setTitle] = useState<string>('');
  const [instructions, setInstructions] = useState<string>('');
  const [circuits, setCircuits] = useState<Circuit[]>([]);
  const [exercises, setExercises] = useState<DayExercise[]>([]);
  const [exercisesList, setExercisesList] = useState<ExerciseTitle[]>([]);

  const { sendRequest: getExercisesTitle } = useApi<ExerciseTitle[]>();

  const fetchExercisesTitleList = () => {
    getExercisesTitle({
      callback: (data: ExerciseTitle[] | null, error: string | null) => {
        if (data) {
          setExercisesList(data);
        }
      },
      command: ApiCommand.GET,
      url: urlConstants.exercises.getExercisestitle,
    });
  };

  useEffect(() => {
    if (isOpen) {
      fetchExercisesTitleList();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedWarmup) {
      setTitle(selectedWarmup.title || '');
      setInstructions(selectedWarmup.instructions || '');
      setCircuits(selectedWarmup.circuits || []);
      setExercises(selectedWarmup.exercises || []);
    } else {
      setTitle('');
      setInstructions('');
      setCircuits([]);
      setExercises([]);
    }
  }, [selectedWarmup, isOpen]);

  // Circuit actions
  const addCircuit = () => {
    const nextTypeId = circuits.length + 1;
    const newCircuit: Circuit = {
      rounds: 1,
      title: `Circuit ${nextTypeId}`,
      typeId: nextTypeId,
      formats: [],
      circuitExercises: [],
    };
    setCircuits([...circuits, newCircuit]);
  };

  const removeCircuit = (circuitIndex: number) => {
    const list = [...circuits];
    list.splice(circuitIndex, 1);
    list.forEach((c, idx) => {
      c.typeId = idx + 1;
      if (c.title?.startsWith('Circuit ') || c.title?.startsWith('Warmup ')) {
        c.title = `Circuit ${idx + 1}`;
      }
    });
    setCircuits(list);
  };

  const updateCircuitField = (circuitIndex: number, key: keyof Circuit, value: any) => {
    const list = [...circuits];
    list[circuitIndex] = { ...list[circuitIndex], [key]: value };
    setCircuits(list);
  };

  const addExerciseToCircuit = (circuitIndex: number) => {
    const list = [...circuits];
    const newExercise: DayExercise = {
      exerciseId: '',
      guide: '',
      sets: 1,
      reps: 0,
      weight: 0,
      rest: 0,
      per_side: false,
      custom_text: '',
      status: '',
      extra: [],
    };
    list[circuitIndex] = {
      ...list[circuitIndex],
      circuitExercises: [...list[circuitIndex].circuitExercises, newExercise],
    };
    setCircuits(list);
  };

  const removeExerciseFromCircuit = (circuitIndex: number, exerciseIndex: number) => {
    const list = [...circuits];
    const exercisesList = [...list[circuitIndex].circuitExercises];
    exercisesList.splice(exerciseIndex, 1);
    list[circuitIndex] = {
      ...list[circuitIndex],
      circuitExercises: exercisesList,
    };
    setCircuits(list);
  };

  const updateExerciseInCircuit = (circuitIndex: number, exerciseIndex: number, key: keyof DayExercise, value: any) => {
    const list = [...circuits];
    const exercisesList = [...list[circuitIndex].circuitExercises];
    exercisesList[exerciseIndex] = { ...exercisesList[exerciseIndex], [key]: value };
    list[circuitIndex] = {
      ...list[circuitIndex],
      circuitExercises: exercisesList,
    };
    setCircuits(list);
  };

  // Stand-alone exercise actions
  const addStandaloneExercise = () => {
    const newExercise: DayExercise = {
      exerciseId: '',
      guide: '',
      sets: 1,
      reps: 0,
      weight: 0,
      rest: 0,
      per_side: false,
      custom_text: '',
      status: '',
      extra: [],
    };
    setExercises([...exercises, newExercise]);
  };

  const removeStandaloneExercise = (index: number) => {
    const list = [...exercises];
    list.splice(index, 1);
    setExercises(list);
  };

  const updateStandaloneExercise = (index: number, key: keyof DayExercise, value: any) => {
    const list = [...exercises];
    list[index] = { ...list[index], [key]: value };
    setExercises(list);
  };

  const exerciseOptions = exercisesList.map(({ title, id, _id }) => ({
    label: title,
    value: id || _id || '',
  }));

  const isFormInvalid = !title;

  return (
    <StrongModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={() => onSubmit(title, instructions, circuits, exercises)}
      title={selectedWarmup ? 'Edit Warmup Template' : 'Create Warmup Template'}
      isSubmitDisabled={isFormInvalid}
      size="4xl"
      body={
        <VStack spacing={4} align='stretch'>
          <FormControl isRequired>
            <FormLabel color='gray.600'>Warmup Title</FormLabel>
            <StrongInput
              placeholder='e.g. Upper Body Dynamic Warmup'
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </FormControl>

          <FormControl>
            <FormLabel color='gray.600'>Instructions</FormLabel>
            <StrongTextarea
              placeholder='Add warmup instructions here...'
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={4}
            />
          </FormControl>

          <Box maxH="600px" overflowY="auto" pr={2}>
            {/* Stand-alone Exercises Section */}
            <Box borderTop="1px solid" borderColor="gray.200" pt={4} mb={6}>
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="sm" color="gray.700">Warmup Exercises (Stand-alone)</Heading>
                <Button
                  size="sm"
                  leftIcon={<AddIcon />}
                  colorScheme="primary"
                  variant="solid"
                  onClick={addStandaloneExercise}
                >
                  Add Exercise
                </Button>
              </Flex>

              {exercises.length === 0 ? (
                <Box textAlign="center" py={6} bg="gray.50" borderRadius="md" border="1px dashed" borderColor="gray.300" mb={4}>
                  <Text color="gray.500" fontSize="sm">No stand-alone exercises added yet.</Text>
                </Box>
              ) : (
                exercises.map((exercise, index) => {
                  const selectedId = normalizeExerciseId(exercise.exerciseId);
                  const selectedOption = exerciseOptions.find(opt => opt.value === selectedId) || undefined;

                  return (
                    <Box key={index} p={4} borderWidth="1px" borderRadius="md" bg="white" shadow="sm" mb={4}>
                      <Flex justifyContent="space-between" alignItems="center" mb={3}>
                        <Text fontWeight="bold" color="primary.500" fontSize="sm">Exercise #{index + 1}</Text>
                        <IconButton
                          aria-label="Remove Exercise"
                          icon={<DeleteIcon />}
                          colorScheme="red"
                          variant="outline"
                          size="xs"
                          onClick={() => removeStandaloneExercise(index)}
                        />
                      </Flex>
                      <Grid templateColumns="repeat(6, 1fr)" gap={3}>
                        <GridItem colSpan={6}>
                          <FormControl isRequired>
                            <FormLabel fontSize="xs" color="gray.600" mb={1}>Select Base Exercise</FormLabel>
                            <StrongSingleSelect
                              options={exerciseOptions}
                              value={selectedOption}
                              onSelectChange={(val) => updateStandaloneExercise(index, 'exerciseId', val)}
                            />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={6}>
                          <FormControl>
                            <FormLabel fontSize="xs" color="gray.600" mb={1}>Guideline / Instructions</FormLabel>
                            <StrongTextarea
                              value={exercise.guide || ''}
                              onChange={(e) => updateStandaloneExercise(index, 'guide', e.target.value)}
                              placeholder="Instructions..."
                              rows={2}
                              height="50px"
                            />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <FormControl>
                            <FormLabel fontSize="xs" color="gray.600" mb={1}>Sets</FormLabel>
                            <StrongInput
                              type="number"
                              min={1}
                              value={exercise.sets}
                              onChange={(e) => updateStandaloneExercise(index, 'sets', Number(e.target.value) || 1)}
                            />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <FormControl>
                            <FormLabel fontSize="xs" color="gray.600" mb={1}>Reps</FormLabel>
                            <StrongInput
                              type="number"
                              value={exercise.reps}
                              onChange={(e) => updateStandaloneExercise(index, 'reps', Number(e.target.value) || 0)}
                            />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <FormControl>
                            <FormLabel fontSize="xs" color="gray.600" mb={1}>Weight (lbs)</FormLabel>
                            <StrongInput
                              type="number"
                              value={exercise.weight}
                              onChange={(e) => updateStandaloneExercise(index, 'weight', Number(e.target.value) || 0)}
                            />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <FormControl>
                            <FormLabel fontSize="xs" color="gray.600" mb={1}>Rest (sec)</FormLabel>
                            <StrongInput
                              type="number"
                              value={exercise.rest}
                              onChange={(e) => updateStandaloneExercise(index, 'rest', Number(e.target.value) || 0)}
                            />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={4} display="flex" alignItems="center">
                          <StrongCheckBox
                            isChecked={exercise.per_side || false}
                            onCheckChange={(checked) => updateStandaloneExercise(index, 'per_side', checked)}
                          >
                            Per Side
                          </StrongCheckBox>
                        </GridItem>
                        <GridItem colSpan={6}>
                          <FormControl>
                            <FormLabel fontSize="xs" color="gray.600" mb={1}>Custom Text (optional)</FormLabel>
                            <StrongInput
                              value={exercise.custom_text || ''}
                              onChange={(e) => updateStandaloneExercise(index, 'custom_text', e.target.value)}
                              placeholder="Optional custom text..."
                            />
                          </FormControl>
                        </GridItem>
                      </Grid>
                    </Box>
                  );
                })
              )}
            </Box>

            {/* Circuits Section */}
            <Box borderTop="1px solid" borderColor="gray.200" pt={4}>
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="sm" color="gray.700">Warmup Circuits</Heading>
                <Button
                  size="sm"
                  leftIcon={<AddIcon />}
                  colorScheme="primary"
                  variant="outline"
                  onClick={addCircuit}
                >
                  Add Circuit
                </Button>
              </Flex>

              {circuits.length === 0 ? (
                <Box textAlign="center" py={6} bg="gray.50" borderRadius="md" border="1px dashed" borderColor="gray.300">
                  <Text color="gray.500" fontSize="sm">No circuits added yet.</Text>
                </Box>
              ) : (
                circuits.map((c, circuitIndex) => (
                  <WarmupCircuitEditor
                    key={circuitIndex}
                    circuit={c}
                    circuitIndex={circuitIndex}
                    exerciseOptions={exerciseOptions}
                    onUpdate={(key, value) => updateCircuitField(circuitIndex, key, value)}
                    onAddExercise={() => addExerciseToCircuit(circuitIndex)}
                    onRemoveExercise={(exIdx) => removeExerciseFromCircuit(circuitIndex, exIdx)}
                    onUpdateExercise={(exIdx, key, value) => updateExerciseInCircuit(circuitIndex, exIdx, key, value)}
                    onRemoveCircuit={() => removeCircuit(circuitIndex)}
                  />
                ))
              )}
            </Box>
          </Box>
        </VStack>
      }
    />
  );
};

export default EditWarmupModal;
